import { Inject, Injectable } from '@angular/core';
import { Observable, catchError, filter, map, switchMap, tap, throwError } from 'rxjs';
import { HttpStreamsService, UserLimitReachedError } from '@cbre/http';
import { ConfirmDialogComponent, DialogService } from '@cbre/components';
import { Translator, TRANSLATOR } from '@miq/utils';
import { UserActionTrackerService, UserActionIds } from '@miq/utils/metrics';
import { INFO_USER_RESOLVER_TOKEN, UserInfoResolver } from '../../../stacking-plan.token';
import { Executable } from '../executable';
import { ExportExecutableParams, SP_REPORT_NAME_BY_TYPES } from './export.model';
import { EXPORT_RESOLVER_TOKEN, ExportResolver } from './export-resolvers/export-resolver.model';
import { StackPlanViewStateService } from '../../../state/sp-view-state.service';

/**
 * Service responsible for exporting data as an executable.
 */
@Injectable()
export class ExportExecutableService implements Executable {
  /**
   * Constructs an instance of ExportExecutableService.
   * @param userInfoResolver The user info resolver.
   * @param translator The translator.
   * @param httpClient The HTTP client.
   * @param excelExportService The Excel export service.
   * @param notificationService The notification service.
   * @param dialogService The dialog service.
   */
  constructor(
    @Inject(INFO_USER_RESOLVER_TOKEN) private userInfoResolver: UserInfoResolver,
    @Inject(TRANSLATOR) private translator: Translator,
    @Inject(EXPORT_RESOLVER_TOKEN) private exportResolver: ExportResolver<ExportExecutableParams, boolean>,
    private httpClient: HttpStreamsService,
    // dialog service opens newest material dialog module (not legacy )
    private dialogService: DialogService,
    private userActionTrackerService: UserActionTrackerService,
    private viewStateService: StackPlanViewStateService
  ) {}

  execute(params: ExportExecutableParams): Observable<any> {
    const { propertyId } = params;
    const nextParams = Object.assign({ viewState: this.viewStateService.getState() }, params);
    let fileDeliveryHandler$ = this.exportResolver.resolve(nextParams);

    // Check for the ExportResolver and send the data
    // Just do this logic to check and load allowance
    return this._registerAndCheckDownloadAllowance$(propertyId).pipe(
      filter(Boolean),
      // track user metrics
      tap(() => this.#trackUserAction(nextParams)),
      switchMap(() => fileDeliveryHandler$)
    );
  }

  private _registerAndCheckDownloadAllowance$(propertyId: number): Observable<any> {
    const { id: userId, emailAddress } = this.userInfoResolver.resolve();
    const payload = { records: [propertyId], createdBy: emailAddress };
    // log user exported file
    return this.httpClient.httpPost(`/exports/${userId}/stackingplan`, payload).pipe(
      map(() => true),
      catchError(err => {
        if (err instanceof UserLimitReachedError) {
          return this.httpClient.httpGet(`/users/${userId}/exportlimits`).pipe(
            switchMap(limits => {
              const params = { limit: limits.stackingplan, asked: 1 };
              const data = {
                title: this.translator.translate('errorMessages.userLimitReached'),
                messages: [this.translator.translate('errorMessages.exportLimitError', params)],
              };
              const options = { panelClass: 'alert-modals' };
              return this.dialogService.openDialog(ConfirmDialogComponent, data, options).pipe(map(() => false));
            })
          );
        }
        // throw if any other error
        return throwError(() => err);
      })
    );
  }

  /**
   * Track the user intention to download an SP report.
   * @param params {ExportExecutableParams}
   */
  #trackUserAction = (params: ExportExecutableParams) => {
    const actionId = UserActionIds.DownloadReport;
    const actionDetails = {
      // excel / pdf
      outputFormat: params.dialogOptions.fileType,
      reportType: SP_REPORT_NAME_BY_TYPES[params.exportType],
      reportId: params.propertyId,
      reportName: 'Stacking Plan',
      actionTaken: 'Download Report',
    };
    // user info logged in Server from token
    this.userActionTrackerService.registerAction({ actionId, actionDetails });
  };
}
